(() => {
  const parseJSON = (el) => {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || "null");
    } catch {
      return null;
    }
  };

  const normalize = (s) => (s || "").toString().toLowerCase().trim();

  /** Slug territory labels from metaobjects to compare with sidebar region_key values. */
  const territorySlug = (raw) => {
    let s = normalize(raw)
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
    if (!s) return "";
    const aliases = {
      eu: "europe",
      sam: "south-america",
      latam: "south-america",
      "latin-america": "south-america",
      carib: "caribbean",
      cam: "central-america",
      "middle-east-north-africa": "middle-east",
      mena: "middle-east",
      me: "middle-east",
      gcc: "middle-east",
      asean: "asia",
      "south-east-asia": "asia",
      "southeast-asia": "asia",
      "east-asia": "asia",
      "far-east": "asia",
      india: "asia",
      "south-asia": "asia",
      "sub-saharan": "africa",
      "sub-saharan-africa": "africa",
      ssa: "africa"
    };
    return aliases[s] || s;
  };

  /**
   * Sidebar "Middle East and Africa" also shows Europe + Middle East + Asia (per territory tags),
   * plus Africa-tagged staff so the label still fits.
   */
  const MIDDLE_EAST_AFRICA_TERRITORIES = new Set([
    "europe",
    "asia",
    "middle-east",
    "middle-east-asia",
    "middle-east-and-asia",
    "middle-east-and-africa",
    "middle-east-africa",
    "africa",
    "north-africa",
    "east-africa",
    "west-africa",
    "western-africa",
    "southern-africa",
    "sub-saharan-africa",
    "central-africa",
    "emea",
    "mena",
    "apac"
  ]);

  const repTerritoryMatchesSidebarKey = (canon, normalizedSidebarKey) => {
    if (!normalizedSidebarKey) return true;
    if (!canon) return false;
    if (normalizedSidebarKey === "middle-east-africa") {
      return MIDDLE_EAST_AFRICA_TERRITORIES.has(canon);
    }
    return canon === normalizedSidebarKey;
  };

  /** Split admin text: commas, semicolons, slashes, newlines, or " and " between regions */
  const parseTerritoryKeys = (raw) => {
    const s = (raw || "").toString().trim();
    if (!s) return [];
    const parts = s
      .split(/\s*(?:,|;|\/|\n|\r\n|\s+and\s+)\s*/i)
      .map((x) => x.trim())
      .filter(Boolean);
    const slugs = parts.map((p) => territorySlug(p)).filter(Boolean);
    return [...new Set(slugs)];
  };

  const repMatchesSidebarKey = (territoryKeyList, normalizedSidebarKey) => {
    if (!normalizedSidebarKey) return true;
    const list = Array.isArray(territoryKeyList) ? territoryKeyList : [];
    if (!list.length) return false;
    return list.some((c) => repTerritoryMatchesSidebarKey(c, normalizedSidebarKey));
  };

  const motionOk = () => {
    try {
      return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return true;
    }
  };

  const createEl = (tag, attrs = {}, children = []) => {
    const el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => {
      if (v === null || v === void 0) return;
      if (k === "class") el.className = v;
      else if (k === "text") el.textContent = v;
      else el.setAttribute(k, v);
    });
    children.forEach((c) => el.appendChild(c));
    return el;
  };

  const initials = (name) => {
    const n = (name || "").trim();
    if (!n) return "?";
    return n.slice(0, 2).toUpperCase();
  };

  const flipFromRects = (firstRects, elements) => {
    if (!motionOk() || !elements.length) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        elements.forEach((el) => {
          const fr = firstRects.get(el);
          if (!fr) return;
          const lr = el.getBoundingClientRect();
          const dx = fr.left - lr.left;
          const dy = fr.top - lr.top;
          if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
          el.style.transition = "none";
          el.style.transform = `translate(${dx}px, ${dy}px)`;
          requestAnimationFrame(() => {
            el.style.transition = "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)";
            el.style.transform = "";
            const clean = () => {
              el.style.transition = "";
              el.removeEventListener("transitionend", clean);
            };
            el.addEventListener("transitionend", clean, { once: true });
          });
        });
      });
    });
  };

  const createPersonTile = (rep, onPersonClick) => {
    const keys = Array.isArray(rep.territoryKeys) && rep.territoryKeys.length
      ? rep.territoryKeys
      : parseTerritoryKeys(rep.regionKey);
    const keyAttr = (keys.length ? keys : ["office-staff"]).join(" ");
    const article = createEl("article", {
      class: "rep-person-tile",
      "data-rep-id": String(rep.id),
      "data-territory-keys": keyAttr,
      "aria-label": rep.name || "Representative"
    });

    // Core click should NOT navigate to `/pages/team/...` (region selection instead).
    const core = createEl("a", { class: "rep-person-tile__core", href: "#" });
    if (typeof onPersonClick === "function") {
      core.addEventListener("click", (e) => {
        // Prevent navigation to /pages/team/...; instead mimic selecting that person's region.
        e.preventDefault();
        e.stopPropagation();
        onPersonClick(rep);
      });
    }
    const media = createEl("div", { class: "rep-person-tile__media" });
    if (rep.photo) {
      media.appendChild(
        createEl("img", {
          src: rep.photo,
          alt: rep.name || "",
          loading: "lazy",
          decoding: "async"
        })
      );
    } else {
      media.appendChild(
        createEl("span", { class: "rep-person-tile__placeholder", "aria-hidden": "true", text: initials(rep.name) })
      );
    }
    core.appendChild(media);
    core.appendChild(createEl("span", { class: "rep-person-tile__name", text: rep.name || "" }));
    article.appendChild(core);

    const expand = createEl("div", { class: "rep-person-tile__expand" });
    const roleLine = rep.roleLine || rep.position;
    if (roleLine) {
      expand.appendChild(createEl("p", { class: "rep-person-tile__role", text: roleLine }));
    }
    const contacts = createEl("div", { class: "rep-person-tile__contacts" });
    if (rep.email) contacts.appendChild(createEl("a", { href: `mailto:${rep.email}`, text: rep.email }));
    if (rep.telephone) contacts.appendChild(createEl("a", { href: `tel:${rep.telephone}`, text: rep.telephone }));
    if (rep.cellphone) contacts.appendChild(createEl("a", { href: `tel:${rep.cellphone}`, text: rep.cellphone }));
    if (contacts.children.length) expand.appendChild(contacts);
    article.appendChild(expand);

    return article;
  };

  const initRepLocator = (root) => {
    const data = parseJSON(root.querySelector('script[type="application/json"][data-rep-locator]'));
    if (!data) return;

    const regions = Array.isArray(data.regions) ? data.regions : [];
    const repMatchesAnySidebarKey = (territoryKeys) =>
      regions.some((reg) => repMatchesSidebarKey(territoryKeys, normalize(reg.key)));
    const reps = (Array.isArray(data.reps) ? data.reps : []).map((r) => {
      const parsed = parseTerritoryKeys(r.regionKey);
      let territoryKeys = parsed.length ? parsed : ["office-staff"];
      // If metaobject "position" isn't actually a country/region label (no sidebar matches),
      // file the rep into the Management and Staff bucket instead of hiding them.
      if (!repMatchesAnySidebarKey(territoryKeys))
        territoryKeys = ["office-staff"];
      return {
        id: r.id,
        name: r.name || "",
        regionKey: r.regionKey,
        territoryKeys,
        photo: r.photo || "",
        position: r.position || "",
        roleLine: r.roleLine || "",
        bio: r.bio || "",
        email: r.email || "",
        telephone: r.telephone || "",
        cellphone: r.cellphone || "",
        url: r.url || ""
      };
    });

    const listEl = root.querySelector("[data-rep-regions-list]");
    const detailEl = root.querySelector("[data-rep-detail]");
    const allBtn = root.querySelector("[data-rep-all]");
    if (!listEl || !detailEl) return;

    const getRegionKeyForRep = (rep) => {
      const parsedKeys = parseTerritoryKeys(rep.regionKey);
      const keys =
        Array.isArray(rep.territoryKeys) && rep.territoryKeys.length
          ? rep.territoryKeys
          : parsedKeys.length
            ? parsedKeys
            : ["office-staff"];

      for (const reg of regions) {
        const nk = normalize(reg.key);
        if (repMatchesSidebarKey(keys, nk)) return reg.key;
      }
      return null;
    };

    const introText = (data.emptyMessage || "").trim();
    const useRegionHub = regions.length > 0;
    let activeKey = null;
    let gen = 0;

    const repsForKey = (key) => {
      const nk = normalize(key);
      if (!nk) return reps;
      return reps.filter((r) => repMatchesSidebarKey(r.territoryKeys, nk));
    };

    const tileMatches = (tile, key) => {
      if (!key) return true;
      const nk = normalize(key);
      const raw = (tile.getAttribute("data-territory-keys") || "").trim();
      const keys = raw.split(/\s+/).filter(Boolean);
      return repMatchesSidebarKey(keys, nk);
    };

    const regionKeyExists = (key) => regions.some((r) => normalize(r.key) === normalize(key));

    const updateRootMode = () => {
      root.setAttribute("data-rep-mode", activeKey ? "region" : "all");
      if (useRegionHub) root.setAttribute("data-rep-hub", "true");
      else root.removeAttribute("data-rep-hub");
      if (allBtn) {
        allBtn.classList.toggle("is-active", !activeKey);
        allBtn.setAttribute("aria-pressed", !activeKey ? "true" : "false");
      }
      if (useRegionHub) {
        const grid = detailEl.querySelector("[data-rep-grid]");
        if (grid) grid.setAttribute("aria-hidden", activeKey ? "false" : "true");
      }
    };

    const renderHeader = () => {
      detailEl.querySelectorAll("[data-rep-detail-header]").forEach((n) => n.remove());

      const header = createEl("div", { "data-rep-detail-header": "true" });
      if (!activeKey) {
        header.className = "rep-detail-header rep-detail-header--all";
        if (introText) {
          const introClass = useRegionHub ? "rep-detail-coverage-label" : "rep-detail-intro";
          header.appendChild(createEl("p", { class: introClass, text: introText }));
        }
      } else {
        const regMeta = regions.find((r) => normalize(r.key) === normalize(activeKey));
        const title = (regMeta && regMeta.title) || activeKey;
        const count = repsForKey(activeKey).length;
        header.className = "rep-detail-header rep-detail-header--region";
        header.appendChild(createEl("h2", { class: "rep-detail-title", text: title }));
        header.appendChild(
          createEl("p", {
            class: "rep-detail-meta",
            text: `${count} representative${count === 1 ? "" : "s"} in this region`
          })
        );
      }
      detailEl.insertBefore(header, detailEl.firstChild);
    };

    const getGrid = () => {
      let g = detailEl.querySelector("[data-rep-grid]");
      if (!g) {
        g = createEl("div", { "data-rep-grid": "true", class: "rep-tiles-grid" });
        detailEl.appendChild(g);
      }
      return g;
    };

    const getHub = () => {
      let h = detailEl.querySelector("[data-rep-hub]");
      if (!h) {
        h = createEl("div", {
          "data-rep-hub": "true",
          class: "rep-locator-map-shell",
          "aria-hidden": "true"
        });
        const grid = detailEl.querySelector("[data-rep-grid]");
        if (grid) detailEl.insertBefore(h, grid);
        else detailEl.appendChild(h);
      }
      return h;
    };

    /** Wrap map + tiles in one stack so we can crossfade (CSS uses data-rep-mode). */
    const ensureRepStage = () => {
      if (detailEl.querySelector("[data-rep-stage]")) return;
      const hub = detailEl.querySelector("[data-rep-hub]");
      const grid = detailEl.querySelector("[data-rep-grid]");
      if (!grid) return;
      const stage = createEl("div", {
        "data-rep-stage": "true",
        class: "rep-locator-stage"
      });
      const insertBefore =
        hub && hub.parentNode === detailEl ? hub : grid;
      detailEl.insertBefore(stage, insertBefore);
      if (hub && hub.parentNode === detailEl) stage.appendChild(hub);
      if (grid.parentNode === detailEl) stage.appendChild(grid);
    };

    const renderTerritoryMap = () => {
      if (!useRegionHub) return;
      const hub = getHub();
      hub.innerHTML = "";
      const tpl = root.querySelector("template[data-rep-map-template]");
      if (!tpl || !tpl.content) {
        hub.appendChild(
          createEl("p", {
            class: "rep-detail-intro rep-map-template-missing",
            text: "Territory map could not be loaded."
          })
        );
        return;
      }
      hub.appendChild(tpl.content.cloneNode(true));
    };

    const applyExpandedState = () => {
      const grid = detailEl.querySelector("[data-rep-grid]");
      if (!grid) return;
      grid.querySelectorAll(".rep-person-tile").forEach((tile) => {
        const show = !activeKey ? false : tileMatches(tile, activeKey);
        tile.classList.toggle("rep-tile--expanded", show && !!activeKey);
      });
    };

    const syncTilesToState = async (fromAllView = false) => {
      const myGen = ++gen;
      const grid = getGrid();
      const tiles = Array.from(grid.querySelectorAll(".rep-person-tile"));
      const useMotion = motionOk();

      const willStayVisible = tiles.filter(
        (t) => tileMatches(t, activeKey) && !t.classList.contains("rep-tile--removed")
      );
      const firstRects = new Map(willStayVisible.map((t) => [t, t.getBoundingClientRect()]));

      const leaving = tiles.filter((t) => !tileMatches(t, activeKey) && !t.classList.contains("rep-tile--removed"));
      const entering = tiles.filter((t) => tileMatches(t, activeKey) && t.classList.contains("rep-tile--removed"));

      leaving.forEach((t) => {
        t.classList.remove("rep-tile--entering");
        if (useMotion && !fromAllView) t.classList.add("rep-tile--leaving");
        else {
          t.classList.remove("rep-tile--leaving");
          t.classList.add("rep-tile--removed");
        }
      });

      const leaveMs = useMotion && leaving.length > 0 && !fromAllView ? 400 : 0;
      await new Promise((r) => setTimeout(r, leaveMs));
      if (myGen !== gen) return;

      leaving.forEach((t) => {
        t.classList.remove("rep-tile--leaving");
        t.classList.add("rep-tile--removed");
      });

      entering.forEach((t) => {
        t.classList.remove("rep-tile--removed", "rep-tile--leaving");
        if (useMotion) {
          t.classList.add("rep-tile--entering");
          void t.offsetWidth;
          requestAnimationFrame(() => {
            t.classList.remove("rep-tile--entering");
          });
        }
      });

      applyExpandedState();

      if (useMotion && leaving.length > 0) {
        const persist = willStayVisible.filter((el) => !el.classList.contains("rep-tile--removed"));
        flipFromRects(firstRects, persist);
      }
    };

    const renderSidebar = () => {
      listEl.innerHTML = "";
      regions.forEach((reg) => {
        const k = reg.key;
        const isActive = activeKey !== null && normalize(activeKey) === normalize(k);
        const btn = createEl(
          "button",
          {
            type: "button",
            class: `brand-button${isActive ? " is-active" : ""}`,
            "data-region-key": k,
            role: "option",
            "aria-selected": isActive ? "true" : "false"
          },
          [createEl("span", { class: "brand-name", text: reg.title || k || "Region" })]
        );
        btn.addEventListener("click", () => selectRegion(k));
        listEl.appendChild(createEl("li", { class: "brands-list-item" }, [btn]));
      });
    };

    const selectRegion = (key) => {
      if (key != null && String(key).trim() !== "") {
        if (!regionKeyExists(key)) return;
      } else {
        key = null;
      }
      const prevKey = activeKey;
      activeKey = key;
      const fromAllView = prevKey === null && key !== null;
      renderSidebar();
      updateRootMode();
      renderHeader();
      syncTilesToState(fromAllView);
    };

    if (!reps.length) {
      detailEl.appendChild(
        createEl("p", {
          class: "rep-detail-no-reps",
          text: "No representatives are available yet."
        })
      );
      renderSidebar();
      updateRootMode();
      if (allBtn) {
        allBtn.disabled = true;
        allBtn.classList.add("is-active");
      }
      return;
    }

    const grid = getGrid();
    reps.forEach((rep) =>
      grid.appendChild(
        createPersonTile(rep, () => {
          const rk = getRegionKeyForRep(rep);
          selectRegion(rk);
        })
      )
    );
    if (useRegionHub) {
      renderTerritoryMap();
      ensureRepStage();
    }
    renderHeader();
    updateRootMode();
    renderSidebar();
    applyExpandedState();

    if (allBtn) {
      allBtn.addEventListener("click", () => selectRegion(null));
    }

    listEl.addEventListener("keydown", (e) => {
      const key = e.key;
      if (key !== "ArrowDown" && key !== "ArrowUp" && key !== "Enter") return;
      const buttons = Array.from(listEl.querySelectorAll(".brand-button"));
      if (!buttons.length) return;
      const active = document.activeElement;
      const idx = buttons.indexOf(active);
      if (key === "Enter" && active && active.classList.contains("brand-button")) {
        active.click();
        return;
      }
      e.preventDefault();
      const nextIdx =
        key === "ArrowDown"
          ? Math.min((idx >= 0 ? idx : 0) + 1, buttons.length - 1)
          : Math.max((idx >= 0 ? idx : 0) - 1, 0);
      buttons[nextIdx].focus();
    });
  };

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("[data-rep-locator-root]").forEach(initRepLocator);
  });
})();
